import React, { useState, useEffect } from 'react';
import './Login.css';
import backgroundImage from './assets/images/4k-wallpaper-adorable-blur-boy-1148998-small.jpg';
import { useNavigate } from 'react-router-dom';

function Login() {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkExistingAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const redirectUri = params.get('redirect_uri');
      const state = params.get('state');
      const prompt = params.get('prompt');

      console.log('[OAuth Redirect] checkExistingAuth: start', { redirectUri, state, prompt });

      // If prompt=login, clear existing tokens and force re-authentication
      if (prompt === 'login') {
        console.log('[OAuth Redirect] checkExistingAuth: prompt=login, clearing tokens');
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('communityIFrameUrl');
        setIsCheckingAuth(false);
        return;
      }

      const existingToken = localStorage.getItem('authToken');
      const refreshToken = localStorage.getItem('refreshToken');

      console.log('[OAuth Redirect] checkExistingAuth: token status', {
        hasExistingToken: !!existingToken,
        hasRefreshToken: !!refreshToken
      });

      if (existingToken) {
        try {
          // Verify token is still valid
          const redirectUriParam = redirectUri ? `redirectUri=${encodeURIComponent(redirectUri)}` : '';
          console.log('[OAuth Redirect] checkExistingAuth: calling is-authenticated');
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/native/is-authenticated?${redirectUriParam}`, {
            headers: {
              'Authorization': `Bearer ${existingToken}`
            }
          });

          console.log('[OAuth Redirect] checkExistingAuth: is-authenticated response', { status: response.status, ok: response.ok });

          if (response.ok) {
            const data = await response.json();
            console.log('[OAuth Redirect] checkExistingAuth: is-authenticated data', { isAuthenticated: data.isAuthenticated });
            if (data.isAuthenticated) {
              // Get auth code using existing token
              if (redirectUri) {
                console.log('[OAuth Redirect] checkExistingAuth: authenticated with redirectUri, calling sendAuthToken');
                await sendAuthToken(existingToken, redirectUri, state);
              } else {
                console.log('[OAuth Redirect] checkExistingAuth: authenticated, no redirectUri, navigating to community');
                localStorage.setItem('communityIFrameUrl', data.redirectUri);
                navigate('/community');
              }
              return;
            }
          }

          // If we get here, token is invalid but we might have a valid refresh token
          if (refreshToken) {
            console.log('[OAuth Redirect] checkExistingAuth: token invalid, trying refresh');
            const refreshResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/native/refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                refreshToken: refreshToken,
                redirectUri: redirectUri
              }),
            });

            console.log('[OAuth Redirect] checkExistingAuth: refresh response', { status: refreshResponse.status, ok: refreshResponse.ok });

            if (refreshResponse.ok) {
              const data = await refreshResponse.json();
              localStorage.setItem('authToken', data.accessToken);
              localStorage.setItem('refreshToken', data.refreshToken);
              if (redirectUri) {
                console.log('[OAuth Redirect] checkExistingAuth: refresh success, calling sendAuthToken');
                await sendAuthToken(data.accessToken, redirectUri, state);
              } else {
                console.log('[OAuth Redirect] checkExistingAuth: refresh success, navigating to community');
                localStorage.setItem('communityIFrameUrl', data.redirectUri);
                navigate('/community');
              }
              return;
            } else {
              console.log('[OAuth Redirect] checkExistingAuth: refresh failed, clearing tokens');
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
            }
          }
        } catch (error) {
          console.error('[OAuth Redirect] checkExistingAuth: error', error);
        }
      } else {
        console.log('[OAuth Redirect] checkExistingAuth: no existing token, showing login form');
      }
      setIsCheckingAuth(false);
    };

    checkExistingAuth();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    const params = new URLSearchParams(window.location.search);
    var redirectUri = params.get('redirect_uri');
    const state = params.get('state');

    console.log('[OAuth Redirect] handleSubmit: start', { redirectUri, state });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/native/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ EmailOrPhone: emailOrPhone, Password: password, RedirectUri: redirectUri }),
      });

      console.log('[OAuth Redirect] handleSubmit: login response', { status: response.status, ok: response.ok });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('authToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);

        redirectUri = data.redirectUri;
        console.log('[OAuth Redirect] handleSubmit: login success, redirectUri from server', { redirectUri });

        if (redirectUri) {
          if (state) {
            console.log('[OAuth Redirect] handleSubmit: has state, calling sendAuthToken');
            await sendAuthToken(data.accessToken, redirectUri, state);
          } else {
            console.log('[OAuth Redirect] handleSubmit: no state, navigating to community');
            localStorage.setItem('communityIFrameUrl', data.redirectUri);
            navigate('/community');
          }
        } else {
          console.log('[OAuth Redirect] handleSubmit: no redirectUri from server');
          setErrorMessage('Login failed. Please check your credentials.');
          setIsLoading(false);
        }
      } else {
        console.log('[OAuth Redirect] handleSubmit: login failed');
        setErrorMessage('Login failed. Please check your credentials.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('[OAuth Redirect] handleSubmit: error', error);
      setErrorMessage('An unexpected error occurred. Please try again later.');
      setIsLoading(false);
    }
  };

  const isCustomScheme = (uri) => {
    if (!uri) return false;
    try {
      const parsed = new URL(uri);
      return parsed.protocol !== 'http:' && parsed.protocol !== 'https:';
    } catch {
      return false;
    }
  };

  const sendAuthToken = async (authToken, redirectUri, state) => {
    console.log('[OAuth Redirect] sendAuthToken: start', { redirectUri, state, isCustomScheme: isCustomScheme(redirectUri) });
    try {
      console.log('[OAuth Redirect] sendAuthToken: calling getauthcode');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/oauth/getauthcode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          RedirectUri: redirectUri,
          State: state
        })
      });

      console.log('[OAuth Redirect] sendAuthToken: getauthcode response', { status: response.status, ok: response.ok });

      if (response.ok) {
        const data = await response.json();
        console.log('[OAuth Redirect] sendAuthToken: got redirect_url', { redirectUrl: data.redirect_url?.substring(0, 50) + '...' });

        // For custom URL schemes (e.g. myapp://callback), use the backend's redirect
        // endpoint. Browsers/Electron webviews sometimes block direct navigation to
        // custom schemes; serving an HTML page that does the redirect works more reliably.
        if (isCustomScheme(redirectUri)) {
          const redirectPageUrl = `${process.env.REACT_APP_API_BASE_URL}/oauth/redirect?url=${encodeURIComponent(data.redirect_url)}`;
          console.log('[OAuth Redirect] sendAuthToken: custom scheme - navigating to redirect page', { redirectPageUrl: redirectPageUrl.substring(0, 80) + '...' });
          window.location.href = redirectPageUrl;
          console.log('[OAuth Redirect] sendAuthToken: window.location.href set (navigate should occur)');
        } else {
          console.log('[OAuth Redirect] sendAuthToken: standard scheme - navigating directly to redirect_url');
          window.location.href = data.redirect_url;
          console.log('[OAuth Redirect] sendAuthToken: window.location.href set (navigate should occur)');
        }
      } else {
        const errorData = await response.json();
        console.error('[OAuth Redirect] sendAuthToken: getauthcode failed', errorData);
        setErrorMessage('Authorization failed. Please try again later.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('[OAuth Redirect] sendAuthToken: error', error);
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/native/send-reset-password-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailOrPhone }),
      });

      if (response.ok) {
        setResetEmailSent(true);
      } else {
        setErrorMessage('Failed to send reset instructions. Please try again.');
      }
    } catch (error) {
      console.error('Error sending reset instructions:', error);
      setErrorMessage('An unexpected error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderResetPassword = () => {
    if (resetEmailSent) {
      return (
        <>
          <div>
            If this email address is associated with an account, you will receive a password reset link.
          </div>
          <button type="button" onClick={() => {
            setShowResetPassword(false);
            setResetEmailSent(false);
            setEmailOrPhone('');
          }}>
            Return to Login
          </button>
        </>
      );
    }

    return (
      <>
        <input
          type="email"
          placeholder="Email"
          value={emailOrPhone}
          onChange={(e) => setEmailOrPhone(e.target.value)}
          required
        />
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Sending...' : 'Send Reset Instructions'}
        </button>
        <a href="#" className="reset-link" onClick={(e) => {
          e.preventDefault();
          setShowResetPassword(false);
        }}>Back to Login</a>
      </>
    );
  };

  const isLoginButtonDisabled = () => {
    return isLoading || !emailOrPhone.trim() || !password.trim();
  };

  return (
    <div className="login-container">
      {isCheckingAuth ? (
        <div className="loading-indicator">
          <p>Checking authentication...</p>
          {/* Add a spinner or loading animation here if desired */}
        </div>
      ) : (
        <form className="login-form" onSubmit={showResetPassword ? handleResetPassword : handleSubmit}>
          <img src="https://afterschoolhq.com/Assets/img/ahq-logo-2-1-1.png" alt="Logo" className="login-logo" />
          
          {!showResetPassword ? (
            <>
              <input
                type="email"
                placeholder="Email"
                value={emailOrPhone}
                onChange={(e) => setEmailOrPhone(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="submit" disabled={isLoginButtonDisabled()}>
                {isLoading ? 'Logging in...' : 'Log In'}
              </button>
              <a href="#" className="reset-link" onClick={(e) => {
                e.preventDefault();
                const params = new URLSearchParams(window.location.search);
                navigate(`/signup?redirect_uri=${params.get('redirect_uri') || ''}&state=${params.get('state') || ''}`);
              }}>Don't have an account? Sign Up</a>
              <a href="#" className="reset-link" onClick={(e) => {
                e.preventDefault();
                setShowResetPassword(true);
              }}>Forgot Password?</a>
            </>
          ) : (
            renderResetPassword()
          )}
          
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </form>
      )}
    </div>
  );
}

export default Login; 